<template>
  <div
    class="date-picker"
    :class="{ 'date-picker--block': displayAs === 'input' }"
  >
    <Dropdown
      ref="dropdownRef"
      :is-context-menu="false"
      :close-on-click-away="true"
      :close-on-click-within="false"
      position="bottom-start"
      class="date-picker__dropdown"
      @close="onDropdownClose"
    >
      <template #triggerContent>
        <slot name="triggerContent">
          <!-- Input-style trigger (form fields) -->
          <Input
            v-if="displayAs === 'input'"
            :id="id"
            :model-value="displayLabel"
            icon="calendar"
            readonly
            :readonly-has-styling="false"
            :has-error="hasError"
            :placeholder="placeholder"
          />
          <!-- Button-style trigger (Sentry-like filters) -->
          <button
            v-else
            type="button"
            class="form-control date-picker__trigger"
            :class="{ 'has-error': hasError }"
          >
            <Icon
              type="calendar"
              class="date-picker__trigger-icon"
            />
            <span
              class="date-picker__trigger-label"
              :class="{ 'text-placeholder': !displayLabel }"
            >
              {{ displayLabel || placeholder }}
            </span>
            <Icon
              v-if="showChevron"
              type="chevron-down"
              class="date-picker__trigger-chevron"
            />
          </button>
        </slot>
      </template>

      <!-- Presets panel -->
      <template v-if="panel === 'presets'">
        <DropdownItem
          v-for="preset in presets"
          :key="getPresetId(preset)"
          :close-on-click="closeOnSelect"
          @item-clicked="onPresetClick(preset)"
        >
          <span class="date-picker__item-label">{{ preset.label }}</span>
          <Icon
            v-if="isActivePreset(preset)"
            type="check"
            class="date-picker__item-check"
          />
        </DropdownItem>

        <li
          v-if="allowAbsoluteDate"
          class="date-picker__divider"
        ></li>

        <DropdownItem
          v-if="allowAbsoluteDate"
          :close-on-click="false"
          @item-clicked="openAbsolutePanel"
        >
          <div class="date-picker__absolute-item">
            <div class="date-picker__absolute-title">
              <span class="date-picker__item-label">Absolute date</span>
              <Icon
                v-if="isAbsoluteActive"
                type="check"
                class="date-picker__item-check"
              />
            </div>
            <div
              v-if="isAbsoluteActive && absoluteSubLabel"
              class="date-picker__absolute-sub"
            >
              {{ absoluteSubLabel }}
            </div>
          </div>
          <Icon
            type="chevron-right"
            class="date-picker__absolute-arrow"
          />
        </DropdownItem>
      </template>

      <!-- Calendar (absolute date) panel -->
      <li
        v-else
        class="date-picker__calendar-panel"
        @click.stop
      >
        <DatePickerCalendar
          v-model="draft"
          :mode="mode"
          :min-date="minDate"
          :max-date="maxDate"
          :exclude-dates="excludeDates"
          :max-range="maxRange"
          :show-time-range="showTimeRange"
          :show-time="showTime"
          @select="onCalendarSelect"
        />
        <div
          v-if="showFooter"
          class="date-picker__footer"
        >
          <Button
            v-if="hasPresets"
            label="Back"
            severity="secondary"
            text
            size="sm"
            icon="chevron-left"
            @click="panel = 'presets'"
          />
          <div class="flex-1"></div>
          <Button
            v-if="needsApply"
            label="Apply"
            size="sm"
            :disabled="!isDraftComplete"
            @click="applyDraft"
          />
        </div>
      </li>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { computed, ref, watch } from 'vue'
// components
import Button from '@/components/button/Button.vue'
import DatePickerCalendar from '@/components/date-picker/DatePickerCalendar.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import Input from '@/components/forms/Input.vue'
import Icon from '@/components/icon/Icon.vue'
// composables
import { useDate } from '@/composables/useDate'
// types
import type { DatePickerMode, DatePreset, DateRange } from '@/types/components/dateTime'
// utilities
import { getPresetId, resolvePreset } from '@/utilities/date-time'

/** Props for the Sentry-style dropdown date picker */
const props = withDefaults(
  defineProps<{
    /** Selected value: string for single mode, DateRange for range/week/month modes */
    modelValue?: string | DateRange | null
    /** Selection mode */
    mode?: DatePickerMode
    /** Preset ranges shown as the dropdown list (range-like modes only) */
    presets?: DatePreset[]
    /** Show the 'Absolute date' item that opens the calendar panel (when presets exist) */
    allowAbsoluteDate?: boolean
    /** Trigger appearance: compact button or form input */
    displayAs?: 'button' | 'input'
    /** Trigger label format: human-friendly ('Last 7d') or full dates ('09/06/2026 - 25/06/2026') */
    displayFormat?: 'friendly' | 'full'
    /** Show the chevron on the button trigger */
    showChevron?: boolean
    /** Days before this date are disabled */
    minDate?: string | Date | null
    /** Days after this date are disabled */
    maxDate?: string | Date | null
    /** Specific days that are disabled */
    excludeDates?: Array<string | Date>
    /** Maximum number of days in a range (inclusive) */
    maxRange?: number | null
    /** Show a single time input (single mode) */
    showTime?: boolean
    /** Show start/end time inputs (range-like modes) */
    showTimeRange?: boolean
    /** Marks the trigger as invalid */
    hasError?: boolean
    /** Placeholder when nothing is selected */
    placeholder?: string | null
    /** Close the dropdown as soon as a complete selection is made */
    closeOnSelect?: boolean
    /** Input element id (input trigger only) */
    id?: string | null
  }>(),
  {
    modelValue: null,
    mode: 'range',
    presets: () => [],
    allowAbsoluteDate: true,
    displayAs: 'button',
    displayFormat: 'friendly',
    showChevron: true,
    minDate: null,
    maxDate: null,
    excludeDates: () => [],
    maxRange: null,
    showTime: false,
    showTimeRange: false,
    hasError: false,
    placeholder: 'Select date',
    closeOnSelect: true,
    id: null
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string | DateRange]
  /** Fired when a selection is committed (preset click, complete calendar selection, or Apply) */
  select: [value: string | DateRange]
}>()

const { readableDateRange } = useDate()

const dropdownRef = ref<InstanceType<typeof Dropdown> | null>(null)

/** Whether the mode produces a DateRange value */
const isRangeLike = computed(() => props.mode !== 'single')

/** Presets only apply to range-like modes */
const hasPresets = computed(() => isRangeLike.value && props.presets.length > 0)

/** Whether emitted values include a time component */
const hasTimeComponent = computed(() => (props.mode === 'single' ? props.showTime : props.showTimeRange))

/** Time-enabled selections are committed via the Apply button instead of instantly */
const needsApply = computed(() => hasTimeComponent.value)

/** The footer is shown when there is a Back or Apply action to offer */
const showFooter = computed(() => hasPresets.value || needsApply.value)

/** Active panel: preset list or calendar */
const panel = ref<'presets' | 'absolute'>(hasPresets.value ? 'presets' : 'absolute')

/** Draft selection edited inside the calendar panel, committed on select/Apply */
const draft = ref<string | DateRange | null>(null)

/**
 * Formats a resolved boundary for emission ('YYYY-MM-DD', or with time when enabled).
 */
function formatBoundary(date: Dayjs): string {
  return hasTimeComponent.value ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD')
}

/**
 * Resets the draft from the current model value.
 */
function syncDraftFromModel(): void {
  if (props.modelValue && typeof props.modelValue === 'object') {
    // Strip the preset marker so the calendar treats it as a plain range
    draft.value = { start: props.modelValue.start, end: props.modelValue.end }
  } else {
    draft.value = props.modelValue ?? null
  }
}

syncDraftFromModel()

watch(() => props.modelValue, syncDraftFromModel)

/** Whether the draft is a complete selection that can be applied */
const isDraftComplete = computed(() => {
  // Range-like drafts need both boundaries
  if (isRangeLike.value) {
    return !!draft.value && typeof draft.value === 'object' && !!draft.value.start && !!draft.value.end
  }
  return typeof draft.value === 'string' && draft.value.length > 0
})

/** The currently active preset id on the model, if any */
const activePresetId = computed<string | undefined>(() => {
  // Only range models carry a preset marker
  if (!props.modelValue || typeof props.modelValue !== 'object') return undefined
  return props.modelValue.preset
})

/**
 * Whether the given preset is the active selection.
 */
function isActivePreset(preset: DatePreset): boolean {
  return activePresetId.value === getPresetId(preset)
}

/** Whether an absolute (non-preset) range is the active selection */
const isAbsoluteActive = computed(() => {
  if (!props.modelValue || typeof props.modelValue !== 'object') return false
  return !props.modelValue.preset && !!props.modelValue.start && !!props.modelValue.end
})

/** Compact current-range description under the 'Absolute date' item */
const absoluteSubLabel = computed(() => {
  if (!props.modelValue || typeof props.modelValue !== 'object') return ''
  const { start, end } = props.modelValue
  // Needs a complete range to describe
  if (!start || !end) return ''
  return readableDateRange(start, end)
})

/**
 * Friendly label for a single date: Today / Yesterday / 'D MMM YYYY'.
 */
function friendlySingleLabel(date: Dayjs): string {
  // Special-case the two most common days
  if (date.isSame(dayjs(), 'day')) return 'Today'
  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday'
  return date.format('D MMM YYYY')
}

/** Label shown on the closed trigger */
const displayLabel = computed(() => {
  // Single mode: string model
  if (!isRangeLike.value) {
    if (typeof props.modelValue !== 'string' || !props.modelValue) return ''
    const date = dayjs(props.modelValue)
    if (!date.isValid()) return ''

    if (props.displayFormat === 'friendly') {
      const label = friendlySingleLabel(date)
      return props.showTime ? `${label}, ${date.format('HH:mm')}` : label
    }
    return props.showTime ? date.format('DD/MM/YYYY HH:mm') : date.format('DD/MM/YYYY')
  }

  // Range-like modes: DateRange model
  if (!props.modelValue || typeof props.modelValue !== 'object') return ''
  const { start, end } = props.modelValue
  if (!start || !end) return ''

  if (props.displayFormat === 'friendly') {
    // Prefer the preset's short label when a preset is active
    const activePreset = props.presets.find(preset => getPresetId(preset) === activePresetId.value)
    if (activePreset) return activePreset.shortLabel ?? activePreset.label
    return readableDateRange(start, end)
  }

  const format = hasTimeComponent.value ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'
  return `${dayjs(start).format(format)} - ${dayjs(end).format(format)}`
})

/**
 * Commits a value to the model and optionally closes the dropdown.
 */
function commit(value: string | DateRange, close: boolean): void {
  emit('update:modelValue', value)
  emit('select', value)
  if (close) dropdownRef.value?.closeDropdown()
}

/**
 * Handles a preset click: resolve boundaries now and commit with the preset id.
 */
function onPresetClick(preset: DatePreset): void {
  const { start, end } = resolvePreset(preset)
  const value: DateRange = {
    start: formatBoundary(start),
    end: formatBoundary(end),
    preset: getPresetId(preset)
  }
  // DropdownItem handles closing via its close-on-click prop
  commit(value, false)
}

/**
 * Opens the calendar panel with a fresh draft from the current model.
 */
function openAbsolutePanel(): void {
  syncDraftFromModel()
  panel.value = 'absolute'
}

/**
 * Handles a complete calendar selection: commit immediately unless Apply is required.
 */
function onCalendarSelect(value: string | DateRange): void {
  // Time-enabled flows wait for the Apply button
  if (needsApply.value) return
  commit(value, props.closeOnSelect)
}

/**
 * Applies the current draft (Apply button).
 */
function applyDraft(): void {
  // Guard against incomplete drafts
  if (!isDraftComplete.value || !draft.value) return
  commit(draft.value, true)
}

/**
 * Resets panel and draft when the dropdown closes.
 */
function onDropdownClose(): void {
  panel.value = hasPresets.value ? 'presets' : 'absolute'
  syncDraftFromModel()
}
</script>

<style>
.date-picker {
  display: inline-block;
}

.date-picker--block {
  display: block;
  width: 100%;
}

.date-picker--block .dropdown-wrapper {
  display: block;
  width: 100%;
}

.date-picker--block .dropdown-wrapper > div {
  width: 100%;
}

.date-picker__trigger {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  cursor: pointer;
  white-space: nowrap;
  width: auto;
}

.date-picker__trigger-icon {
  color: var(--text-tertiary);
}

.date-picker__trigger-label {
  font-weight: 500;
}

.date-picker__trigger-chevron {
  color: var(--text-tertiary);
  font-size: var(--text-sm);
  margin-left: var(--s-1);
}

.date-picker__item-label {
  font-size: var(--text-sm);
}

.date-picker__item-check {
  color: var(--fg-brand);
  margin-left: var(--s-3);
}

.date-picker__divider {
  border-top: 1px solid var(--border-secondary);
  margin: var(--s-1) 0;
}

.date-picker__absolute-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-picker__absolute-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.date-picker__absolute-sub {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
}

.date-picker__absolute-arrow {
  color: var(--text-quaternary);
  font-size: var(--text-sm);
  margin-left: var(--s-3);
}

.date-picker__calendar-panel {
  display: flex;
  flex-direction: column;
  padding: var(--s-2);
}

.date-picker__footer {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  margin-top: var(--s-2);
  padding-top: var(--s-2);
  border-top: 1px solid var(--border-secondary);
}
</style>
